import { Navbar } from "../components/Navbar";
import { Footer } from "../components/Footer";
import { useAuth } from "../hooks/useAuth";
import { useBlogs } from "../hooks/useBlogs";
import { usePageMeta } from "../hooks/usePageMeta";
import { BlogCard } from "../components/BlogCard";
import { useState, useEffect, useMemo } from "react";
import avatar3 from "../assets/avatar-dev-3.png";

// 11th June 2018 10:00:00
const startDate = new Date("2018-06-11T10:00:00");

export function Home() {
    const { isAuthenticated } = useAuth();
    const { blogs, loading, error } = useBlogs(isAuthenticated ? undefined : "public");
    const [activeTab, setActiveTab] = useState<string>("blogs");

    usePageMeta({ path: "/" });

    const categories = useMemo(() => {
        return Array.from(
            new Set(
                blogs.map(blog => (blog.tags && blog.tags.category) ? blog.tags.category : 'blogs')
            )
        ).sort();
    }, [blogs]);

    useEffect(() => {
        if (categories.length > 0 && !categories.includes(activeTab)) {
            setActiveTab(categories[0]!);
        }
    }, [categories, activeTab]);

    const filteredBlogs = useMemo(() => {
        return blogs.filter(blog => {
            const category = (blog.tags && blog.tags.category) ? blog.tags.category : 'blogs';
            return category === activeTab;
        });
    }, [blogs, activeTab]);

    const [elapsedTime, setElapsedTime] = useState({
        years: 0,
        months: 0,
        days: 0,
        hours: 0,
        minutes: 0,
        seconds: 0
    });

    useEffect(() => {
        const calculateTime = () => {
            const now = new Date();
            let years = now.getFullYear() - startDate.getFullYear();
            let months = now.getMonth() - startDate.getMonth();
            let days = now.getDate() - startDate.getDate();
            let hours = now.getHours() - startDate.getHours();
            let minutes = now.getMinutes() - startDate.getMinutes();
            let seconds = now.getSeconds() - startDate.getSeconds();

            if (seconds < 0) {
                seconds += 60;
                minutes--;
            }
            if (minutes < 0) {
                minutes += 60;
                hours--;
            }
            if (hours < 0) {
                hours += 24;
                days--;
            }
            if (days < 0) {
                // Get days in previous month
                const previousMonth = new Date(now.getFullYear(), now.getMonth(), 0);
                days += previousMonth.getDate();
                months--;
            }
            if (months < 0) {
                months += 12;
                years--;
            }

            setElapsedTime({ years, months, days, hours, minutes, seconds });
        };

        calculateTime();
        const timer = setInterval(calculateTime, 1000);

        return () => clearInterval(timer);
    }, []);

    return (
        <div className="min-h-screen bg-base-100 text-base-content font-sans flex flex-col">
            <Navbar />

            {/* Hero / About Me Section */}
            <section className="relative pt-20 pb-20 w-full bg-gradient-to-br from-base-200 via-base-100 to-base-300">
                <div className="container mx-auto px-4 max-w-5xl flex flex-col-reverse md:flex-row items-center gap-12">
                    <div className="flex-1 space-y-6 text-center md:text-left">
                        <div className="badge badge-primary badge-outline mb-2">Software enthusiast</div>
                        <h1 className="text-5xl md:text-7xl font-extrabold tracking-tight leading-tight">
                            Hi, I'm{" "}
                            <span
                                className="text-primary tooltip tooltip-bottom cursor-help"
                                data-tip="Shonei is not my real name but a username I have been using since 2008"
                            >
                                Shonei
                            </span>
                            .
                        </h1>
                        <p className="text-lg md:text-xl text-base-content/70 leading-relaxed max-w-2xl">
                            A developer for the past {elapsedTime.years}y {elapsedTime.months}m {elapsedTime.days}d {elapsedTime.hours}h {elapsedTime.minutes}m {elapsedTime.seconds}s trying to navigate the world.
                            Doer of stuff. 6am on a Sunday outage survivor.
                        </p>
                    </div>

                    <div className="relative group">
                        <div className="absolute -inset-1 bg-gradient-to-r from-primary to-secondary rounded-full blur opacity-25 group-hover:opacity-75 transition duration-1000 group-hover:duration-200" />
                        <div className="relative w-48 h-48 md:w-64 md:h-64 rounded-full overflow-hidden border-4 border-base-100 shadow-2xl">
                            <img
                                src={avatar3}
                                alt="Profile"
                                className="w-full h-full object-cover"
                            />
                        </div>
                    </div>
                </div>
            </section>

            {/* Blog List Section */}
            <main className="flex-1 py-16 container mx-auto px-4 max-w-6xl">
                <div className="flex flex-col gap-12">
                    <div className="flex items-end justify-between border-b border-base-content/10 pb-6">
                        <div>
                            <h2 className="text-3xl font-bold">Latest Articles</h2>
                        </div>
                    </div>

                    {loading ? (
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                            {[1, 2, 3].map((i) => (
                                <div key={i} className="flex flex-col gap-4">
                                    <div className="skeleton h-48 w-full rounded-2xl"></div>
                                    <div className="skeleton h-4 w-28"></div>
                                    <div className="skeleton h-4 w-full"></div>
                                    <div className="skeleton h-4 w-full"></div>
                                </div>
                            ))}
                        </div>
                    ) : error ? (
                        <div className="alert alert-error">
                            <span>Error loading blogs: {error.message}</span>
                        </div>
                    ) : (
                        <div className="flex flex-col gap-6">
                            {categories.length > 0 && (
                                <div role="tablist" className="tabs tabs-box self-start flex-wrap gap-1 bg-base-200/50 p-2 rounded-xl">
                                    {categories.map(category => (
                                        <button
                                            key={category}
                                            role="tab"
                                            className={`tab rounded-lg px-4 py-2 font-medium transition-colors ${activeTab === category ? 'tab-active bg-primary text-primary-content' : 'hover:bg-base-300'}`}
                                            onClick={() => setActiveTab(category)}
                                        >
                                            {category.charAt(0).toUpperCase() + category.slice(1)}
                                        </button>
                                    ))}
                                </div>
                            )}
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                                {filteredBlogs.length > 0 ? (
                                    filteredBlogs.map((blog) => (
                                        <BlogCard
                                            key={blog.id}
                                            blog={blog}
                                            showEdit={isAuthenticated}
                                        />
                                    ))
                                ) : (
                                    <div className="col-span-full text-center py-20 opacity-50">
                                        <p className="text-xl">
                                            {blogs.length === 0 
                                                ? "No posts found. Start writing something amazing!"
                                                : "No posts found in this category."}
                                        </p>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                </div>
            </main>

            <Footer />
        </div>
    );
}
